import { createContext, ReactNode, useEffect, useReducer } from 'react';
import jwtDecode from 'jwt-decode';
// config
import { HOST_API } from '../config';
// utils
import axios, { AxiosRequestTransformer } from 'axios';
// @types
import { ActionMap, AuthAplication, AuthState, AuthUser, JWTContextType } from '../@type/auth';
import { ApolloClient, ApolloProvider, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { GraphQLWsLink  } from '@apollo/client/link/subscriptions';
import { createClient } from "graphql-ws";

// ----------------------------------------------------------------------
enum Types {
    Initial = 'INITIALIZE',
    Login = 'LOGIN',
    Logout = 'LOGOUT',
    Refresh = 'REFRESH'
}

type JWTAuthPayload = {
    [Types.Initial]: {
        isAuthenticated: boolean;
        user: AuthUser;
        environment: string;
    };
    [Types.Login]: {
        user: AuthUser;
        environment: string;
    };
    [Types.Logout]: undefined;
    [Types.Refresh]: {
        user: AuthUser;
        environment: string;
    }
};

const areEqualUser = (p: AuthUser | null, u: AuthUser | null) => {
    if (!p || !u) return false;
    if (p.FullName !== u.FullName || p.FullName !== u.FullName) return false;
    if (p.Applications?.length !== u.Applications?.length) return false;
    if (!p.Applications || !u.Applications) return false;
    let comparer = p.Applications.reduce((acc: AuthAplication[], val: AuthAplication): AuthAplication[] => {
        let e = u.Applications.find(a => a.ApplicationCode === val.ApplicationCode && a.Role === val.Role);
        if (e)
            acc.push(e);
        return acc;
    }, []);
    return comparer.length === p.Applications.length;
}

const isValidToken = (accessToken: string) => !(!accessToken);

const decodeUser = (userToken: string): AuthUser =>
    jwtDecode<AuthUser>(userToken);
const decodeEnvironment = (environmentToken: string): string =>
    jwtDecode<{ environment: string }>(environmentToken).environment;
// ----------------------------------------------------------------------
function padTo2Digits(num: number) {
    return num.toString().padStart(2, '0');
}

function padTo3Digits(num: number) {
    return num.toString().padStart(3, '0');
}

function formatDate(date: Date) {
    return `${padTo2Digits(date.getFullYear())}-${padTo2Digits(date.getMonth() + 1)}-${padTo2Digits(date.getDate())}T${padTo2Digits(date.getHours())}:${padTo2Digits(date.getMinutes())}.${padTo3Digits(date.getMilliseconds())}Z`
}

const dateTransformer: AxiosRequestTransformer = data => {
    if (data instanceof Date) {
        // do your specific formatting here
        return formatDate(data);
    }
    if (Array.isArray(data)) {
        return data.map(val => dateTransformer(val))
    }
    if (typeof data === "object" && data !== null) {
        var a: any = {};

        Object.entries(data).map(([key, val]) =>
            a[key] = dateTransformer(val));

        return a;
    }
    return data
}

const transformRequest = axios.defaults.transformRequest ?
    axios.defaults.transformRequest instanceof Array ?
        [dateTransformer].concat(axios.defaults.transformRequest) :
        [dateTransformer, axios.defaults.transformRequest] :
    dateTransformer;

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
    environment: ''
};

const axiosInstance = axios.create({
    baseURL: HOST_API,
    transformRequest: transformRequest,
});
let loc = window.location, new_uri:string, host:string;
if (loc.protocol === "https:") {
    new_uri = "wss:";
} else {
    new_uri = "ws:";
}
host = loc.host;
if(loc.host.includes('localhost:'))
    host = 'localhost:1337';
new_uri += '//' + host;

let wsLink : GraphQLWsLink= new GraphQLWsLink(createClient({
    url: new_uri,
    lazy: true,
    connectionParams: () => ({
        authorization: `Bearer ${localStorage.getItem('accessToken')}`
    })
}));
let client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    link: wsLink,
    cache: new InMemoryCache()
});

const JWTReducer = (state: AuthState, action: JWTActions) => {
    switch (action.type) {
        case 'INITIALIZE':
            return {
                isAuthenticated: action.payload.isAuthenticated,
                isInitialized: true,
                user: action.payload.user,
                environment: action.payload.environment
            };
        case 'LOGIN':
            return {
                ...state,
                isAuthenticated: true,
                user: action.payload.user,
                environment: action.payload.environment
            };
        case 'REFRESH':
            if (!areEqualUser(state.user, action.payload.user) || state.environment !== action.payload.environment)
                return {
                    ...state,
                    isAuthenticated: true,
                    user: action.payload.user,
                    environment: action.payload.environment
                }
            return state;
        case 'LOGOUT':
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                environment: ''
            };

        default:
            return state;
    }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
    children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
    const [state, dispatch] = useReducer(JWTReducer, initialState);

    const setSession = (accessToken: string | null) => {
        if (accessToken) {
            localStorage.setItem('accessToken', accessToken);
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
            localStorage.removeItem('accessToken');
            delete axiosInstance.defaults.headers.common.Authorization;
        }
    };

    const errorReponseHandler = (error: { response: { status: number; data: any; }; }) => {
        
        if (error.response.status === 440)
            refreshToken('', null, '');
        else if (error.response.status === 403 && !window.location.href.endsWith('/403'))
            window.location.href = '/403'; 
        else if (error.response.status === 500 && !window.location.href.endsWith('/500'))
            window.location.href = '/500';
        else
            return Promise.reject((error.response && error.response.data) || 'Something went wrong')
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                axiosInstance.interceptors.response.use(
                    (response) => {
                        if (!response.config.url?.includes('/mid/auth/')) {
                            const token = response.headers['x-refresh'];
                            const user = decodeUser(response.headers['x-t360']);
                            const environment = decodeEnvironment(response.headers['x-t360-e']);
                            refreshToken(token, user, environment);
                        }
                        return response
                    },
                    (error) => errorReponseHandler(error),
                );


                axiosInstance.interceptors.request.use((config) => {
                    const accessToken = localStorage.getItem('accessToken');
                    if (accessToken) {
                        config.headers = config.headers ?? {};
                        (config.headers as Record<string, string>).Authorization = `Bearer ${accessToken}`;
                    }
                    return config;
                });

                const accessToken = localStorage.getItem('accessToken');

                if (accessToken && isValidToken(accessToken)) {
                    setSession(accessToken);
                    await axiosInstance.get('/mid/auth/validate').then((response) => {
                        const { user, environment } = response.data;

                        dispatch({
                            type: Types.Initial,
                            payload: {
                                isAuthenticated: true,
                                user: decodeUser(user),
                                environment: decodeEnvironment(environment)
                            },
                        });
                        
                    }).catch(errorReponseHandler)

                } else {
                    dispatch({
                        type: Types.Initial,
                        payload: {
                            isAuthenticated: false,
                            user: null,
                            environment: ''
                        },
                    });
                }
            } catch (err) {
                dispatch({
                    type: Types.Initial,
                    payload: {
                        isAuthenticated: false,
                        user: null,
                        environment: ''
                    },
                });
            }
        };

        initialize();
    }, []);

    const refreshToken = (token: string, user: AuthUser | null, environment: string) => {
        if (isValidToken(token)) {
            setSession(token);
            //comparar
            dispatch({
                type: Types.Refresh,
                payload: {
                    user: user,
                    environment: environment
                },
            });
        }
        else {
            logout();
        }
    };

    const login = async (email: string, password: string) => {
        const response = await axiosInstance.post('/mid/auth/login', {
            email,
            password,
        });


        const { error, accessToken, user, environment } = response.data;
        if (error) {
            throw new Error(error);
        }
        if (isValidToken(accessToken) && user) {
            setSession(accessToken.token);
            dispatch({
                type: Types.Login,
                payload: {
                    user: decodeUser(user),
                    environment: decodeEnvironment(environment)
                }
            });
        }
        else {

        }
    };

    const logout = async () => {
        setSession(null);
        dispatch({ type: Types.Logout });
    };

    const accessToken = localStorage.getItem('accessToken');

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'jwt',
                login,
                logout,
                axios: axiosInstance
            }}
        >
            {accessToken !== null ? (
                <ApolloProvider client={client} >
                    {children}
                </ApolloProvider>
            ) : (<>{children }</>)}
        </AuthContext.Provider>
    );
}

export { AuthContext, AuthProvider };
